import React, { useEffect, useState, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useUpdateCommunity } from 'queries/community.query'
import Dropzone from '../../settings/components/Dropzone'
import { getStorageImageUrl } from 'utils/imageUrl'

// Helper function to get the full image URL from a relative path
const getFullImageUrl = relativePath => getStorageImageUrl(relativePath)

export const EditCommunityModal = ({ isOpen, onClose, community }) => {
  // State for non-file fields
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
  })

  // State for files and previews
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)

  const [formErrors, setFormErrors] = useState({})
  const toast = useToast()

  const { mutate: updateCommunity, isPending } = useUpdateCommunity()

  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  // --- Input Change Handler for Text Fields ---
  const handleInputChange = e => {
    const { name, value } = e.target
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  // 1. Corrected Dropzone File Change Handler for Logo (using useCallback at the top level)
  const handleLogoChange = useCallback(file => {
    setLogoPreview(prevPreview => {
      // Revoke the old local blob URL if it exists
      if (prevPreview && prevPreview.startsWith('blob:')) {
        URL.revokeObjectURL(prevPreview)
      }
      setLogoFile(file)
      return file ? URL.createObjectURL(file) : null
    })
  }, []) // Empty dependency array, as setLogoFile/setLogoPreview are state setters

  // 2. Corrected Dropzone File Change Handler for Photo (using useCallback at the top level)
  const handlePhotoChange = useCallback(file => {
    setPhotoPreview(prevPreview => {
      // Revoke the old local blob URL if it exists
      if (prevPreview && prevPreview.startsWith('blob:')) {
        URL.revokeObjectURL(prevPreview)
      }
      setPhotoFile(file)
      return file ? URL.createObjectURL(file) : null
    })
  }, []) // Empty dependency array, as setPhotoFile/setPhotoPreview are state setters

  // --- Initial Load Effect & Cleanup ---
  useEffect(() => {
    if (community) {
      // 1. Initialize text fields
      setEditFormData({
        name: community.name || '',
        description: community.description || '',
      })
      setFormErrors({})

      // 2. Initialize file previews with existing remote URLs and clear local files
      setLogoFile(null)
      setLogoPreview(getFullImageUrl(community.logo))

      setPhotoFile(null)
      setPhotoPreview(getFullImageUrl(community.photo))
    }

    // 3. Cleanup function: runs on unmount or before dependencies change
    return () => {
      // Clean up any remaining blob URLs when the component unmounts
      if (logoPreview && logoPreview.startsWith('blob:'))
        URL.revokeObjectURL(logoPreview)
      if (photoPreview && photoPreview.startsWith('blob:'))
        URL.revokeObjectURL(photoPreview)
    }
  }, [community])

  // --- Validation ---
  const validateForm = () => {
    const errors = {}
    if (!editFormData.name.trim()) {
      errors.name = 'Community name is required.'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // --- Update Handler ---
  const handleUpdateCommunity = e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const dataToUpdate = new FormData()
    dataToUpdate.append('name', editFormData.name)
    dataToUpdate.append('description', editFormData.description)

    // Append new files from state
    if (logoFile) {
      dataToUpdate.append('logo', logoFile)
    }
    if (photoFile) {
      dataToUpdate.append('photo', photoFile)
    }

    // Append the _method field for Laravel to recognize this as a PUT request
    dataToUpdate.append('_method', 'PUT')

    updateCommunity(
      { id: community.id, updatedCommunityData: dataToUpdate },
      {
        onSuccess: () => {
          // Clean up local blob URLs after successful upload
          if (logoPreview && logoPreview.startsWith('blob:'))
            URL.revokeObjectURL(logoPreview)
          if (photoPreview && photoPreview.startsWith('blob:'))
            URL.revokeObjectURL(photoPreview)

          onClose()
          toast({
            title: 'Community Updated.',
            description: 'The community has been successfully updated.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
        },
        onError: err => {
          toast({
            title: 'Failed to Update Community.',
            description:
              err.response?.data?.message ||
              'There was an error updating the community',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
        },
      },
    )
  }

  if (!community) {
    return null
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Community</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleUpdateCommunity}>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel>Name</FormLabel>
                <Input
                  type='text'
                  name='name'
                  value={editFormData.name}
                  onChange={handleInputChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {formErrors.name && (
                  <Text color='red.500' fontSize='sm' mt={1}>
                    {formErrors.name}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name='description'
                  value={editFormData.description}
                  onChange={handleInputChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Logo (Upload to change)</FormLabel>
                <Dropzone
                  title='Drag or click to upload new logo'
                  onFileChange={handleLogoChange}
                  preview={logoPreview}
                  size='sm'
                />
              </FormControl>

              <FormControl>
                <FormLabel>Photo (Upload to change)</FormLabel>
                <Dropzone
                  title='Drag or click to upload new photo'
                  onFileChange={handlePhotoChange}
                  preview={photoPreview}
                />
              </FormControl>
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme='blue'
            type='submit'
            isLoading={isPending}
            onClick={handleUpdateCommunity}
          >
            Save
          </Button>
          <Button colorScheme='gray' onClick={onClose} ml={3}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
